import type { PackagingOption, PackagingOptions, StorePackageTypeDraft } from "@khazzin/api-client";
import { formatMoney } from "@khazzin/i18n/money";
import { ErrorState } from "@khazzin/ui/patterns/page";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import {
	Button,
	Drawer,
	Field,
	Input,
	NumberInput,
	Select,
	SkeletonText,
	useToast,
} from "@khazzin/ui/primitives";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";

/**
 * Settings → Packaging (bug list, Packaging): the store's default package —
 * Khazzin's Bag until it picks another — and its own branded packaging. Each
 * order packed in one of its own takes one off its count, and at or under the
 * alert level the store is told to send more. A carton that holds N of another
 * type adds N of that type when received.
 */
const SHAPES = ["bag", "box", "mailer", "envelope", "tube", "other"] as const;
type Shape = (typeof SHAPES)[number];
const KINDS = ["plastic", "cardboard", "paper", "fabric", "other"] as const;
type Kind = (typeof KINDS)[number];

const ALERT_TONE = { ok: "success", low: "warning", critical: "danger" } as const;

export function PackagingTab({ storeId, writable }: { storeId: string; writable: boolean }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const toast = useToast();
	const queryClient = useQueryClient();
	const key = ["portal", storeId, "packaging"] as const;
	const packaging = useQuery({
		queryKey: key,
		queryFn: ({ signal }) => api.getPortalPackaging({ signal }),
		enabled: storeId !== "",
	});
	const change = useMutation({
		mutationFn: (run: () => Promise<PackagingOptions>) => run(),
		onSuccess: (next) => {
			queryClient.setQueryData(key, next);
			toast.success({ title: t("portal.settings.packaging.saved") });
		},
		onError: (error) =>
			toast.error({ title: error instanceof Error ? error.message : String(error) }),
	});
	const [editing, setEditing] = useState<PackagingOption | "new" | null>(null);
	const [receiving, setReceiving] = useState<PackagingOption | null>(null);

	if (packaging.isPending) {
		return <SkeletonText lines={6} />;
	}
	if (packaging.isError) {
		return <ErrorState error={packaging.error} t={t} onRetry={() => void packaging.refetch()} />;
	}
	const data = packaging.data;
	const own = data.items.filter((item) => item.ownerStoreId === storeId);
	const khazzin = data.items.filter((item) => item.ownerStoreId !== storeId);
	const name = (item: PackagingOption) => (locale === "ar" ? item.nameAr : item.name);
	const busy = !writable || change.isPending;

	return (
		<div className="flex flex-col gap-5">
			<p className="text-[12.5px] text-fg-muted">{t("portal.settings.packaging.body")}</p>

			<section aria-labelledby="packaging-default" className="flex flex-col gap-2">
				<h3 id="packaging-default" className="text-[13px] font-bold">
					{t("portal.settings.packaging.default")}
				</h3>
				<Field label={t("portal.settings.packaging.defaultLabel")}>
					{(control) => (
						<Select
							{...control}
							value={data.defaultPackageTypeId ?? ""}
							disabled={busy}
							options={data.items.map((item) => ({ value: item.packageTypeId, label: name(item) }))}
							onValueChange={(id) => change.mutate(() => api.setPortalPackagingDefault(id))}
						/>
					)}
				</Field>
			</section>

			<section aria-labelledby="packaging-own" className="flex flex-col gap-2">
				<div className="flex items-center justify-between gap-2">
					<h3 id="packaging-own" className="text-[13px] font-bold">
						{t("portal.settings.packaging.own")}
					</h3>
					{writable && (
						<Button size="sm" variant="secondary" onClick={() => setEditing("new")}>
							{t("portal.settings.packaging.add")}
						</Button>
					)}
				</div>
				{own.length === 0 ? (
					<p className="text-[12.5px] text-fg-muted">{t("portal.settings.packaging.noOwn")}</p>
				) : (
					<ul className="flex flex-col divide-y divide-border rounded-md border border-border">
						{own.map((item) => (
							<li
								key={item.packageTypeId}
								className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-[13px]"
							>
								<div className="flex min-w-0 flex-1 flex-col">
									<span className="font-semibold">{name(item)}</span>
									<span className="text-[12px] text-fg-muted">
										{describe(item, data.items, t, name)}
									</span>
								</div>
								{item.own && (
									<>
										<StatusChip tone={ALERT_TONE[item.own.alertState]} size="sm">
											{t("portal.settings.packaging.onHand", {
												count: item.own.onHand,
												alertAt: item.own.alertAt,
											})}
										</StatusChip>
										<span className="tabular-nums text-fg-muted">
											{formatMoney(
												{ amountMinor: item.own.costMinor, currency: item.currency },
												locale,
											)}
										</span>
									</>
								)}
								{writable && (
									<div className="flex gap-1">
										<Button size="sm" variant="ghost" onClick={() => setReceiving(item)}>
											{t("portal.settings.packaging.receive")}
										</Button>
										<Button size="sm" variant="ghost" onClick={() => setEditing(item)}>
											{t("portal.settings.packaging.edit")}
										</Button>
										<Button
											size="sm"
											variant="ghost"
											disabled={busy}
											onClick={() =>
												change.mutate(() => api.archivePortalPackageType(item.packageTypeId))
											}
										>
											{t("portal.settings.packaging.archive")}
										</Button>
									</div>
								)}
							</li>
						))}
					</ul>
				)}
			</section>

			<section aria-labelledby="packaging-khazzin" className="flex flex-col gap-2">
				<h3 id="packaging-khazzin" className="text-[13px] font-bold">
					{t("portal.settings.packaging.khazzin")}
				</h3>
				<ul className="flex flex-col divide-y divide-border rounded-md border border-border">
					{khazzin.map((item) => (
						<li key={item.packageTypeId} className="flex items-center gap-4 px-3 py-2 text-[13px]">
							<div className="flex min-w-0 flex-1 flex-col">
								<span className="font-semibold">{name(item)}</span>
								<span className="text-[12px] text-fg-muted">
									{describe(item, data.items, t, name)}
								</span>
							</div>
							<span className="tabular-nums">
								{formatMoney({ amountMinor: item.priceMinor, currency: item.currency }, locale)}
							</span>
						</li>
					))}
				</ul>
			</section>

			{editing !== null && (
				<TypeDrawer
					item={editing === "new" ? null : editing}
					children_={own.filter(
						(o) => editing === "new" || o.packageTypeId !== editing.packageTypeId,
					)}
					busy={change.isPending}
					onClose={() => setEditing(null)}
					onSave={(draft) =>
						change.mutate(
							() =>
								editing === "new"
									? api.createPortalPackageType(draft)
									: api.updatePortalPackageType(editing.packageTypeId, draft),
							{ onSuccess: () => setEditing(null) },
						)
					}
				/>
			)}
			{receiving !== null && (
				<ReceiveDrawer
					item={receiving}
					busy={change.isPending}
					onClose={() => setReceiving(null)}
					onSave={(quantity) =>
						change.mutate(
							() => api.receivePortalPackagingStock(receiving.packageTypeId, quantity),
							{
								onSuccess: () => setReceiving(null),
							},
						)
					}
				/>
			)}
		</div>
	);
}

type T = ReturnType<typeof useLocale>["t"];

/** "Bag · 30 × 20 × 2 cm · up to 3,000 g", and what a mother holds. */
function describe(
	item: PackagingOption,
	all: PackagingOption[],
	t: T,
	name: (o: PackagingOption) => string,
) {
	const parts: string[] = [];
	if (item.shape) {
		parts.push(t(`portal.settings.packaging.shape.${item.shape}`));
	}
	if (item.materialKind) {
		parts.push(t(`portal.settings.packaging.kind.${item.materialKind}`));
	}
	if (item.innerLengthCm) {
		parts.push(`${item.innerLengthCm} × ${item.innerWidthCm} × ${item.innerHeightCm} cm`);
	}
	if (item.maxContentWeightG) {
		parts.push(t("portal.settings.packaging.upTo", { grams: item.maxContentWeightG }));
	}
	const child = all.find((o) => o.packageTypeId === item.childPackageTypeId);
	if (child && item.childCount) {
		parts.push(t("portal.settings.packaging.holds", { count: item.childCount, name: name(child) }));
	}
	return parts.join(" · ");
}

function TypeDrawer({
	item,
	children_,
	busy,
	onClose,
	onSave,
}: {
	item: PackagingOption | null;
	children_: PackagingOption[];
	busy: boolean;
	onClose: () => void;
	onSave: (draft: StorePackageTypeDraft) => void;
}) {
	const { t } = useLocale();
	const [draft, setDraft] = useState<StorePackageTypeDraft>({
		name: item?.name ?? "",
		nameAr: item?.nameAr ?? "",
		shape: (item?.shape as Shape | null) ?? "bag",
		materialKind: (item?.materialKind as Kind | null) ?? "plastic",
		innerLengthCm: item?.innerLengthCm ?? 0,
		innerWidthCm: item?.innerWidthCm ?? 0,
		innerHeightCm: item?.innerHeightCm ?? 0,
		maxContentWeightG: item?.maxContentWeightG ?? 0,
		costMinor: item?.own?.costMinor ?? 0,
		alertAt: item?.own?.alertAt ?? 0,
		childPackageTypeId: item?.childPackageTypeId ?? "",
		childCount: item?.childCount ?? 0,
	});
	const set = (next: Partial<StorePackageTypeDraft>) => setDraft({ ...draft, ...next });
	const num = (field: keyof StorePackageTypeDraft) => ({
		value: String(draft[field] ?? 0),
		onChange: (e: { target: { value: string } }) => set({ [field]: Number(e.target.value) || 0 }),
	});
	const mother = draft.childPackageTypeId !== "";
	return (
		<Drawer
			open
			onOpenChange={(open) => !open && onClose()}
			title={item ? t("portal.settings.packaging.edit") : t("portal.settings.packaging.add")}
			closeLabel={t("ui.actions.close")}
			footer={
				<Button
					variant="primary"
					disabled={busy || draft.name.trim() === "" || draft.nameAr.trim() === ""}
					onClick={() => onSave(draft)}
				>
					{t("portal.settings.packaging.save")}
				</Button>
			}
		>
			<div className="flex flex-col gap-3">
				<Field label={t("portal.settings.packaging.name")}>
					{(control) => (
						<Input
							{...control}
							value={draft.name}
							onChange={(e) => set({ name: e.target.value })}
						/>
					)}
				</Field>
				<Field label={t("portal.settings.packaging.nameAr")}>
					{(control) => (
						<Input
							{...control}
							dir="rtl"
							value={draft.nameAr}
							onChange={(e) => set({ nameAr: e.target.value })}
						/>
					)}
				</Field>
				<Field label={t("portal.settings.packaging.shapeLabel")}>
					{(control) => (
						<Select
							{...control}
							value={draft.shape}
							options={SHAPES.map((s) => ({
								value: s,
								label: t(`portal.settings.packaging.shape.${s}`),
							}))}
							onValueChange={(shape) => set({ shape: shape as Shape })}
						/>
					)}
				</Field>
				<Field label={t("portal.settings.packaging.kindLabel")}>
					{(control) => (
						<Select
							{...control}
							value={draft.materialKind ?? "plastic"}
							options={KINDS.map((k) => ({
								value: k,
								label: t(`portal.settings.packaging.kind.${k}`),
							}))}
							onValueChange={(kind) => set({ materialKind: kind as Kind })}
						/>
					)}
				</Field>
				<div className="grid grid-cols-3 gap-2">
					{(["innerLengthCm", "innerWidthCm", "innerHeightCm"] as const).map((field) => (
						<Field key={field} label={t(`portal.settings.packaging.${field}`)}>
							{(control) => (
								<NumberInput {...control} integerOnly min={1} unit="cm" {...num(field)} />
							)}
						</Field>
					))}
				</div>
				<Field label={t("portal.settings.packaging.maxWeight")}>
					{(control) => (
						<NumberInput {...control} integerOnly min={1} unit="g" {...num("maxContentWeightG")} />
					)}
				</Field>
				<Field label={t("portal.settings.packaging.cost")}>
					{(control) => <NumberInput {...control} integerOnly min={0} {...num("costMinor")} />}
				</Field>
				<Field label={t("portal.settings.packaging.holdsLabel")}>
					{(control) => (
						<Select
							{...control}
							value={draft.childPackageTypeId ?? ""}
							options={[
								{ value: "", label: t("portal.settings.packaging.holdsNothing") },
								...children_.map((o) => ({ value: o.packageTypeId, label: o.name })),
							]}
							onValueChange={(id) =>
								set({ childPackageTypeId: id, childCount: id === "" ? 0 : draft.childCount || 2 })
							}
						/>
					)}
				</Field>
				{mother ? (
					<Field label={t("portal.settings.packaging.childCount")}>
						{(control) => <NumberInput {...control} integerOnly min={2} {...num("childCount")} />}
					</Field>
				) : (
					<Field label={t("portal.settings.packaging.alertAt")}>
						{(control) => <NumberInput {...control} integerOnly min={0} {...num("alertAt")} />}
					</Field>
				)}
			</div>
		</Drawer>
	);
}

function ReceiveDrawer({
	item,
	busy,
	onClose,
	onSave,
}: {
	item: PackagingOption;
	busy: boolean;
	onClose: () => void;
	onSave: (quantity: number) => void;
}) {
	const { t } = useLocale();
	const [quantity, setQuantity] = useState(0);
	return (
		<Drawer
			open
			onOpenChange={(open) => !open && onClose()}
			title={t("portal.settings.packaging.receiveTitle", { name: item.name })}
			closeLabel={t("ui.actions.close")}
			footer={
				<Button variant="primary" disabled={busy || quantity < 1} onClick={() => onSave(quantity)}>
					{t("portal.settings.packaging.receive")}
				</Button>
			}
		>
			<Field
				label={t("portal.settings.packaging.quantity")}
				hint={
					item.childCount
						? t("portal.settings.packaging.receiveMother", { count: item.childCount })
						: undefined
				}
			>
				{(control) => (
					<NumberInput
						{...control}
						integerOnly
						min={1}
						value={String(quantity)}
						onChange={(e) => setQuantity(Number(e.target.value) || 0)}
					/>
				)}
			</Field>
		</Drawer>
	);
}
